import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../../../firebase";

type Mode = "login" | "reset";

const setupPortalAccount = httpsCallable(functions, "setupPortalAccount");

const PortalLogin: React.FC = () => {
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>("login");

  // ログイン用
  const [loginEmail, setLoginEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState("");

  // 初回登録・パスワード再設定用
  const [resetEmail, setResetEmail] = useState("");
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const switchToReset = () => {
    setMode("reset");
    setLoginError("");
    setResetSent(false);
    setResetEmail(loginEmail);
  };

  const switchToLogin = () => {
    setMode("login");
    setResetSent(false);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLoggingIn) return;

    setIsLoggingIn(true);
    setLoginError("");

    const normalizedEmail = loginEmail.trim().toLowerCase();

    try {
      await signInWithEmailAndPassword(auth, normalizedEmail, password);
      navigate("/portal", { replace: true });
    } catch (error) {
      console.error("ポータルログインエラー:", error);
      setLoginError(
        "メールアドレスまたはパスワードが正しくありません。初めてご利用の場合や、パスワードをお忘れの場合は下記からパスワードを設定してください。",
      );
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleSendReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSendingReset) return;

    setIsSendingReset(true);

    const normalizedEmail = resetEmail.trim().toLowerCase();

    try {
      // エントリー時のメールアドレスと一致する場合のみ、内部でログイン用アカウントの
      // 器を用意する（アカウントの有無によって応答を変えないため、失敗しても
      // ここではエラー表示しない）
      await setupPortalAccount({ email: normalizedEmail });
    } catch (error) {
      console.error("ポータルアカウント準備エラー:", error);
    }

    try {
      await sendPasswordResetEmail(auth, normalizedEmail, {
        url: `${window.location.origin}/portal/login`,
      });
    } catch (error) {
      // 登録されていないメールアドレスの場合もここでエラーになるが、
      // メールアドレスの存在有無が外部から分からないよう、表示するメッセージは変えない
      console.error("パスワード再設定メール送信エラー:", error);
    } finally {
      setIsSendingReset(false);
      setResetSent(true);
    }
  };

  if (mode === "reset") {
    return (
      <div className="table-contents">
        <div className="admin-edit entry-form">
          <div className="header-text">
            <h1>グループ用マイページ パスワード設定</h1>
            <p>
              エントリー時に登録した連絡担当者メールアドレスを入力してください。
              <br />
              初めてのご利用時、またはパスワードをお忘れの際は、こちらからパスワードを設定してください。
            </p>
          </div>

          {resetSent ? (
            <>
              <p>
                ご入力いただいたメールアドレスがエントリー時のメールアドレスと一致する場合、パスワード設定用のメールをお送りしました。
                <br />
                メール内のリンクからパスワードを設定のうえ、ログイン画面に戻ってログインしてください。
                <br />
                数分待っても届かない場合は、エントリー時のメールアドレスをご確認のうえ、もう一度お試しください。
              </p>
              <div className="edit-footer">
                <button onClick={switchToLogin}>ログイン画面に戻る</button>
              </div>
            </>
          ) : (
            <form onSubmit={handleSendReset}>
              <div className="form-contents middle-size">
                <label className="form-label required">メールアドレス</label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  placeholder="エントリーに使用したメールアドレス"
                />
              </div>

              <div className="edit-footer">
                <button type="submit" disabled={isSendingReset}>
                  {isSendingReset ? "送信中..." : "パスワード設定メールを送信する"}
                </button>
              </div>

              <p>
                <span className="sub-text" onClick={switchToLogin}>
                  ログイン画面に戻る
                </span>
              </p>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="table-contents">
      <div className="admin-edit entry-form">
        <div className="header-text">
          <h1>グループ用マイページ ログイン</h1>
          <p>
            エントリー時に登録した連絡担当者メールアドレスとパスワードでログインしてください。
          </p>
        </div>

        <form onSubmit={handleLogin}>
          <div className="form-contents middle-size">
            <label className="form-label required">メールアドレス</label>
            <input
              type="email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              required
              placeholder="エントリーに使用したメールアドレス"
            />
          </div>

          <div className="form-contents middle-size">
            <label className="form-label required">パスワード</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {loginError && <p className="error-message">{loginError}</p>}

          <div className="edit-footer">
            <button type="submit" disabled={isLoggingIn}>
              {isLoggingIn ? "ログイン中..." : "ログイン"}
            </button>
          </div>

          <p>
            <span className="sub-text" onClick={switchToReset}>
              初めての方・パスワードをお忘れの方はこちら
            </span>
          </p>
        </form>
      </div>
    </div>
  );
};

export default PortalLogin;
